import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Label } from '../core/label';
import { DatePicker } from '../core/datepicker';
import { Input } from '../core/input';
import {
  toDateTimeInput,
  extractDateFromIso,
  extractTimeFromIso,
  combineDateAndTime,
  isCompleteTime,
} from '@org/utils-client';

type DateTimeFieldProps = {
  /** Field label. Omit it when the surrounding form (e.g. `DynamicForm`) already renders one. */
  label?: string;
  /** `yyyy-MM-ddTHH:mm` string (or ISO) — the same format the form submits. */
  value?: string;
  onChange: (value: string | undefined) => void;
  placeholder?: string;
  /** Disables both the calendar and the time box (e.g. while the form is submitting). */
  disabled?: boolean;
  dateDisabled?: boolean;
  lockedDate?: Date;
  /**
   * Time assumed when a date is picked before any time is typed (e.g. `'00:00'`
   * = "from the start of that day"). Without it the value stays empty until
   * both parts are filled in.
   */
  defaultTime?: string;
};

/**
 * Date + time input: the shared shadcn `DatePicker` (same calendar as every
 * other date field in the apps) next to a time box. Use this — never a native
 * `<input type="datetime-local">` — whenever a form needs a date with a time.
 */
export function DateTimeField({
  label,
  value,
  onChange,
  placeholder,
  disabled = false,
  dateDisabled = false,
  lockedDate,
  defaultTime,
}: DateTimeFieldProps) {
  const [localDate, setLocalDate] = useState<Date | undefined>(() => lockedDate ?? extractDateFromIso(value));
  const [localTime, setLocalTime] = useState<string>(() => extractTimeFromIso(value));

  useEffect(() => {
    setLocalDate(lockedDate ?? extractDateFromIso(value));
    setLocalTime(extractTimeFromIso(value));
  }, [value, lockedDate]);

  const updateValue = useCallback(
    (nextDate: Date | undefined, nextTime: string | undefined) => {
      const effectiveDate = lockedDate ?? nextDate;
      const combined = combineDateAndTime(effectiveDate, nextTime ?? defaultTime);
      onChange(combined ? toDateTimeInput(combined) : undefined);
    },
    [lockedDate, defaultTime, onChange],
  );

  const handleDateChange = (selectedDate: Date | undefined) => {
    if (dateDisabled || lockedDate) {
      return;
    }
    setLocalDate(selectedDate);
    updateValue(selectedDate, localTime || undefined);
  };

  const handleTimeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextTime = event.target.value;
    setLocalTime(nextTime);
    if (!nextTime) {
      updateValue(localDate, undefined);
      return;
    }
    if (isCompleteTime(nextTime)) {
      updateValue(localDate, nextTime);
    }
  };

  return (
    <div className="space-y-2">
      {label ? <Label className="text-xs uppercase text-muted-foreground">{label}</Label> : null}
      <div className="flex flex-col gap-3 md:flex-row md:items-center">
        <DatePicker
          value={localDate}
          onChange={handleDateChange}
          placeholder={placeholder ?? 'Select date'}
          disabled={disabled || dateDisabled || Boolean(lockedDate)}
        />
        <div className="md:w-40">
          <Input
            type="time"
            step={60}
            value={localTime}
            onChange={handleTimeChange}
            disabled={disabled}
            className="bg-background appearance-none [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
          />
        </div>
      </div>
    </div>
  );
}
